import { createPortal } from 'react-dom';
import { useState } from 'react';
import { FaUserCircle } from 'react-icons/fa';
import { Icons } from '@/utils/Icons';
import { profileDrawerItems } from '@/utils/constants';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { useLogoutMutation } from '@/store/api/authApi';
import { useAppDispatch } from '@/hooks';
import { logout } from '@/store';
import { apiSlice } from '@/store/api/apiSlice';
import UnsettledBetModal from '@/components/common/UnsettledBetModal';
import { useIsDemoUser, useAppSelector } from '@/hooks/redux';

interface ProfileDrawerProps {
  open: boolean;
  onClose: () => void;
}

const ProfileDrawer = ({ open, onClose }: ProfileDrawerProps) => {
  const dispatch = useAppDispatch();
  const [logoutApi] = useLogoutMutation();
  const isDemoUser = useIsDemoUser();
  const username = useAppSelector((state) => state.user.profile?.username ?? '');
  const balance = useAppSelector((state) => state.wallet.mainBalance);

  const [exposureModalOpen, setExposureModalOpen] = useState(false);

  const handleClick = () => {
    dispatch(logout());
    dispatch(apiSlice.util.resetApiState());
    logoutApi().catch(() => {});
    window.location.href = '/';
  };
  return createPortal(
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        className={`fixed inset-0 z-[9998] bg-black/50 transition-opacity duration-300 ${
          open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />

      {/* Drawer */}
      <div
        className={`fixed right-0 top-0 z-[9999] h-screen w-[320px] bg-white dark:bg-darkPanelBg shadow-2xl transition-transform duration-300 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Close Button */}
        <button onClick={onClose} className="absolute right-3 top-3 text-black dark:text-white">
          <Icons.Cross2 size={12} />
        </button>

        <div className="pt-14">
          {/* User Info */}
          <div className="flex items-center gap-2 bg-popupProfileBox text-popupProfileText h-10 mx-4 px-[10px] py-[5px] rounded-sm">
            <FaUserCircle size={20} className="text-[#1f2c38] dark:text-white" />

            <h2 className="text-[18px] font-bold text-[#1f2c38] dark:text-white">
              {username} - {balance.toFixed(2)}
            </h2>
          </div>
          {/* Exposure */}
          <div
            onClick={isDemoUser ? undefined : () => setExposureModalOpen(true)}
            className={`flex items-center justify-between rounded bg-exposure-gradient mx-4 my-[5px] px-[10px] py-[5px] text-white gap-[10px] ${isDemoUser ? 'cursor-default' : 'cursor-pointer'}`}
          >
            <span className="text-lg">Exposure</span>
            <span className="text-lg text-[#32d338]">0.00</span>
          </div>
          {/* P&L */}
          <div className="flex items-center justify-between rounded bg-[#1f2c38] mx-4 my-[5px] px-[10px] py-[5px] text-white cursor-pointer gap-[10px]">
            <span className="text-lg">P&amp;L</span>
            <span className="text-lg text-[#32d338]">0.00</span>
          </div>
          {/* Profile Menu */}
          <div className="px-4 py-[10px]">
            {(isDemoUser
              ? profileDrawerItems.filter((item) => item.id === 6)
              : profileDrawerItems
            ).map((item) => (
              <Link
                key={item.id}
                to={item.path}
                onClick={onClose}
                className="p-[3px] mb-[2px] text-sm font-semibold flex items-center hover:bg-[linear-gradient(90deg,#fec733,#18162a00)] rounded-tl-[50px] rounded-bl-[50px]"
              >
                <div className="bg-sideMenuBg size-[35px] rounded-[50px] mr-[7px] flex items-center justify-center">
                  <img className="size-[18px]" src={item.imgUrl} alt="" />
                </div>
                <span className="font-semibold text-[#1f2c38] dark:text-white">{item.title}</span>
              </Link>
            ))}
          </div>
          {/* Logout */}
          <div
            onClick={handleClick}
            className="flex h-10 mx-4 my-[5px] items-center rounded bg-[#781200] px-[10px] py-[5px] text-white transition cursor-pointer hover:opacity-90"
          >
            <img className="size-[18px] mr-[6px]" src="/images/logout.svg" alt="" />
            <span className="font-medium">Logout</span>
          </div>
        </div>
      </div>
      <UnsettledBetModal open={exposureModalOpen} onClose={() => setExposureModalOpen(false)} />
    </>,
    document.body
  );
};

export default ProfileDrawer;
